/**
 * AvocatTemplate — Cabinet d'avocats / Conseil. Sharp, navy power suit.
 * Palette: deep navy (#0B192C) + ivory (#F8F9FA) + gold accent (#D4AF37)
 * Fonts: EB Garamond (display) + DM Sans (body)
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string;
  desc: string;
  product: string;
  city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const c = (f: keyof WebsiteCustomizations, fb: string, cu?: WebsiteCustomizations) => cu?.[f] || fb;

const AvocatTemplate = ({ name, desc, city, customizations, editMode, onCustomize }: Props) => {
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);

  return (
    <div
      className="min-h-screen"
      style={{ background: "#0B192C", color: "#F8F9FA", fontFamily: "'DM Sans', sans-serif" }}
      data-testid="template-avocat"
    >
      {/* Nav */}
      <header className="border-b border-[#D4AF37]/20 px-8 md:px-16 py-6 flex items-center justify-between">
        <EditableText
          as="div"
          value={name || "Villard & Associés"}
          onChange={set("logoText")}
          editMode={editMode}
          data-field="logoText"
          style={{ fontFamily: "'EB Garamond', serif", fontSize: "22px", fontWeight: 600, letterSpacing: "0.02em", color: "#D4AF37" }}
        />
        <nav className="hidden md:flex items-center gap-10 text-[13px] tracking-[0.2em] uppercase">
          <span className="cursor-pointer hover:text-[#D4AF37] transition-colors">Cabinet</span>
          <span className="cursor-pointer hover:text-[#D4AF37] transition-colors">Compétences</span>
          <span className="cursor-pointer hover:text-[#D4AF37] transition-colors">Équipe</span>
          <span className="cursor-pointer hover:text-[#D4AF37] transition-colors">Contact</span>
        </nav>
        <button className="text-[12px] tracking-[0.25em] uppercase px-5 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B192C] transition-all">
          Prendre RDV
        </button>
      </header>

      {/* Hero — formal, symmetrical */}
      <section className="px-8 md:px-16 py-28 md:py-40 text-center border-b border-[#D4AF37]/15" style={{ background: "linear-gradient(180deg, #0B192C 0%, #132743 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-10">
            <span className="w-16 h-px bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.5em] uppercase" style={{ color: "#D4AF37" }}>Depuis 1982</span>
            <span className="w-16 h-px bg-[#D4AF37]" />
          </div>
          <EditableText
            as="h1"
            value={c("heroTitle", name || "Un cabinet d'avocats\nd'exception.", customizations)}
            onChange={set("heroTitle")}
            editMode={editMode}
            data-field="heroTitle"
            className="leading-[1.05] mb-10 whitespace-pre-line"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontSize: "clamp(3rem, 7vw, 6rem)",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              color: "#F8F9FA",
            }}
          />
          <EditableText
            as="p"
            value={c("heroSubtitle", desc || "Droit des affaires, contentieux stratégique, arbitrage international. Au service des dirigeants qui n'ont pas le droit à l'erreur.", customizations)}
            onChange={set("heroSubtitle")}
            editMode={editMode}
            data-field="heroSubtitle"
            multiline
            className="text-base md:text-lg opacity-75 max-w-2xl mx-auto leading-relaxed"
            style={{ fontStyle: "italic", fontFamily: "'EB Garamond', serif", fontSize: "22px" }}
          />
          <div className="mt-14">
            <EditableText
              as="button"
              value={c("ctaText", "Consultation confidentielle", customizations)}
              onChange={set("ctaText")}
              editMode={editMode}
              data-field="ctaText"
              className="px-10 py-4 border-2 hover:bg-[#D4AF37] hover:text-[#0B192C] transition-all"
              style={{
                borderColor: "#D4AF37",
                color: "#D4AF37",
                fontSize: "12px",
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            />
          </div>
        </div>
      </section>

      {/* Practice areas */}
      <section className="px-8 md:px-16 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: "#D4AF37" }}>— Compétences</div>
          <h2 className="mb-16" style={{ fontFamily: "'EB Garamond', serif", fontSize: "clamp(2.2rem, 4vw, 3.5rem)", fontWeight: 400 }}>
            Quatre domaines d'excellence.
          </h2>
          <div className="space-y-0 border-t border-[#D4AF37]/15">
            {[
              { n: "01", t: "Droit des affaires", d: "Fusions-acquisitions, structuration, pactes d'actionnaires, private equity." },
              { n: "02", t: "Contentieux stratégique", d: "Représentation devant les juridictions françaises et européennes. Enjeux > 10 M€." },
              { n: "03", t: "Arbitrage international", d: "ICC, LCIA, CIETAC. Plus de 40 arbitrages conclus ces cinq dernières années." },
              { n: "04", t: "Droit fiscal", d: "Optimisation, contentieux fiscal, prix de transfert. Pour dirigeants et familles fortunées." },
            ].map((p) => (
              <div
                key={p.n}
                className="border-b border-[#D4AF37]/15 py-10 grid md:grid-cols-12 gap-6 items-baseline group hover:bg-white/[0.02] transition-colors cursor-pointer"
              >
                <div className="md:col-span-1 text-xs tracking-[0.3em] uppercase opacity-40">{p.n}</div>
                <div className="md:col-span-4" style={{ fontFamily: "'EB Garamond', serif", fontSize: "28px", fontWeight: 500 }}>
                  {p.t}
                </div>
                <div className="md:col-span-6 opacity-75 leading-relaxed">{p.d}</div>
                <div className="md:col-span-1 text-[#D4AF37] text-right opacity-0 group-hover:opacity-100 transition-opacity">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="px-8 md:px-16 py-24 border-t border-[#D4AF37]/15" style={{ background: "#132743" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: "#D4AF37" }}>— Associés</div>
          <h2 className="mb-16" style={{ fontFamily: "'EB Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 400 }}>
            Trois signatures, un engagement commun.
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "Maître Villard", r: "Associé fondateur", s: "Contentieux · Arbitrage" },
              { n: "Maître Laurent", r: "Associée", s: "Affaires · M&A" },
              { n: "Maître Reinhardt", r: "Associé", s: "Fiscalité · Patrimoine" },
            ].map((p, i) => (
              <div key={i} className="border-l-2 border-[#D4AF37]/40 pl-6">
                <div className="aspect-[3/4] mb-6" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.1) 0%, rgba(11,25,44,0.6) 100%)" }}>
                  <div className="h-full flex items-center justify-center" style={{ fontFamily: "'EB Garamond', serif", fontSize: "90px", fontWeight: 400, color: "#D4AF37", opacity: 0.6 }}>
                    {p.n.split(" ")[1]?.charAt(0) || "V"}
                  </div>
                </div>
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "22px", fontWeight: 500 }}>{p.n}</div>
                <div className="text-xs tracking-[0.25em] uppercase opacity-60 mt-2" style={{ color: "#D4AF37" }}>{p.r}</div>
                <div className="text-sm opacity-75 mt-2 italic">{p.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-8 md:px-16 py-14 border-t border-[#D4AF37]/20">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "24px", fontWeight: 600, color: "#D4AF37" }}>
              {name || "Villard & Associés"}
            </div>
            <div className="text-xs mt-2 opacity-60">Avocats au Barreau de {city || "Paris"} · Toque 452</div>
          </div>
          <div className="text-right text-sm space-y-1">
            <div><EditableText as="span" value={c("phone", "01 44 54 02 10", customizations)} onChange={set("phone")} editMode={editMode} data-field="phone" /></div>
            <div className="opacity-70"><EditableText as="span" value={c("email", "contact@villard-avocats.fr", customizations)} onChange={set("email")} editMode={editMode} data-field="email" /></div>
            <div className="opacity-70">28 avenue Montaigne, 75008 {city || "Paris"}</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AvocatTemplate;
